import { useState } from "react";
import { Check, Copy, Loader2, Pencil, Plus, X } from "lucide-react";

const slug = (s) => String(s).replace(/[^a-zA-Z0-9_-]+/g, "_");

// CORE-style year-range line under a model; the owner can correct it in place (both years), applying to every vehicle under it.
export const YearsHead = ({ id, group, years, onEdit, lit = false, on = false }) => {
  const [edit, setEdit] = useState(null); // {ys, ye}
  const [busy, setBusy] = useState(false);
  const tid = `${id}-years-${slug(group)}-${String(years).replace(/[^0-9]+/g, "_")}`;
  const start = (e) => { e.stopPropagation(); const [a, b] = String(years).split(/[–-]/); setEdit({ ys: a || "", ye: b || a || "" }); };
  const save = async () => {
    const ys = parseInt(edit.ys, 10), ye = parseInt(edit.ye, 10);
    if (!ys || !ye || ye < ys) return;
    setBusy(true);
    try { await onEdit(ys, ye); } finally { setBusy(false); setEdit(null); }
  };
  if (edit) {
    const box = "field h-6 w-16 px-1.5 py-0 text-center text-[12px] tabular-nums";
    return (
      <div className="picker-years" data-testid={`${tid}-edit`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") save(); if (e.key === "Escape") setEdit(null); }}>
        <input autoFocus value={edit.ys} inputMode="numeric" maxLength={4} data-testid={`${tid}-start`} onChange={(e) => setEdit({ ...edit, ys: e.target.value.replace(/\D/g, "") })} className={box} />
        <span className="text-[#6b7079]">–</span>
        <input value={edit.ye} inputMode="numeric" maxLength={4} data-testid={`${tid}-end`} onChange={(e) => setEdit({ ...edit, ye: e.target.value.replace(/\D/g, "") })} className={box} />
        <button type="button" data-testid={`${tid}-save`} disabled={busy} onClick={save} title="Save (Enter)" className="rounded p-0.5 text-[#3ecf8e] hover:bg-white/10 disabled:opacity-40">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
        <button type="button" data-testid={`${tid}-cancel`} onClick={() => setEdit(null)} title="Cancel (Esc)" className="rounded p-0.5 text-[#9aa0a8] hover:bg-white/10"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <div className={`picker-years group/years ${lit ? "is-lit" : ""} ${on ? "is-on" : ""}`} data-testid={tid} data-lit={lit || undefined}>
      <span className="picker-pop">{years}</span>
      <span className="picker-years-line" aria-hidden="true" />
      {onEdit && <button type="button" tabIndex={-1} data-testid={`${tid}-edit-btn`} onClick={start} title="Correct this year range (applies to every vehicle listed under it)"
        className="rounded-md p-0.5 text-[#6b7079] opacity-0 transition-opacity hover:bg-[#4c9ffe]/20 hover:text-[#4c9ffe] group-hover/years:opacity-100"><Pencil className="h-3 w-3" /></button>}
    </div>
  );
};

// Owner adds a trim under a year range: trim name + body style. The trim becomes its own vehicle listed under that name only
// (no extra "All Trims" row) with a copy of every template here — so its glass can be changed later without touching All Trims.
export const AddTrimRow = ({ id, group, years, bodies = [], bodyStyles = [], className, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [trim, setTrim] = useState("");
  const [body, setBody] = useState(bodies[0] || "");
  const [busy, setBusy] = useState(false);
  const tid = `${id}-add-trim-${slug(group)}-${String(years).replace(/[^0-9]+/g, "_")}`;
  const names = [...new Set([...bodies, ...bodyStyles])];
  const save = async () => {
    const t = trim.trim();
    if (!t || !body) return;
    setBusy(true);
    try { await onAdd(t, body); setOpen(false); setTrim(""); } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <div className={`${className} picker-addtrim !cursor-default`} data-testid={tid}>
        <button type="button" tabIndex={-1} data-testid={`${tid}-btn`} onClick={(e) => { e.stopPropagation(); setBody(bodies[0] || ""); setOpen(true); }}
          className="flex items-center gap-1 text-[11px] text-[#6b7079] transition-colors hover:text-[#4c9ffe]"><Plus className="h-3 w-3" /> Add trim</button>
      </div>
    );
  }
  const stop = (e) => { e.stopPropagation(); if (e.key === "Escape") setOpen(false); if (e.key === "Enter") save(); };
  return (
    <div role="option" className={`${className} !cursor-default is-hi`} data-testid={`${tid}-form`} onClick={(e) => e.stopPropagation()} onKeyDown={stop}>
      <span className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <input autoFocus value={trim} placeholder="Trim, e.g. Premium" data-testid={`${tid}-name`} onChange={(e) => setTrim(e.target.value)} className="field h-6 w-[7.5rem] flex-none px-1.5 py-0 text-[12px]" />
        <select value={body} data-testid={`${tid}-body`} onChange={(e) => { if (e.target.value === "__other") { const n = (window.prompt("Body style", body) || "").trim(); if (n) setBody(n); } else setBody(e.target.value); }}
          className="field h-6 w-[7rem] min-w-0 flex-none py-0 text-[12px]">
          {!body && <option value="">Body style…</option>}
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
          <option value="__other">Type another…</option>
        </select>
      </span>
      <button type="button" data-testid={`${tid}-save`} disabled={busy || !trim.trim() || !body} onClick={save} title="Add (Enter)" className="rounded p-0.5 text-[#3ecf8e] hover:bg-white/10 disabled:opacity-40">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
      <button type="button" data-testid={`${tid}-cancel`} onClick={() => setOpen(false)} title="Cancel (Esc)" className="rounded p-0.5 text-[#9aa0a8] hover:bg-white/10"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
};

// Owner changes a generation row's body type in place ("2022–2026  SUV" → pencil → type it). Free text with the known body types as
// suggestions; Enter / ✓ save, Esc / X cancel.
export const BodyEditRow = ({ id, option: o, bodyStyles = [], className, onSave, onCancel }) => {
  const [val, setVal] = useState(o.body || "");
  const names = [...bodyStyles];
  if (o.body && !names.includes(o.body)) names.unshift(o.body);
  const v = slug(o.value), list = `${id}-body-list-${v}`;
  const save = () => { const n = val.trim(); if (n && n !== o.body) onSave(n); else onCancel(); };
  return (
    <div role="option" data-testid={`${id}-edit-body-row-${v}`} className={`${className} !cursor-default`} onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Escape") onCancel(); if (e.key === "Enter") save(); }}>
      <span className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <input autoFocus list={list} value={val} maxLength={40} placeholder="Body type…" data-testid={`${id}-edit-body-input-${v}`} onChange={(e) => setVal(e.target.value)}
          onFocus={(e) => e.target.select()} className="field h-6 w-[9.5rem] min-w-0 flex-none py-0 text-[12px]" />
        <datalist id={list}>{names.map((n) => <option key={n} value={n} />)}</datalist>
        <span className="truncate text-[11px] text-[#6b7079]">{(o.trims || []).filter((t) => !t.allTrims).map((t) => t.trim).join(" · ")}</span>
      </span>
      <button type="button" data-testid={`${id}-edit-body-save-${v}`} onMouseDown={(e) => e.preventDefault()} onClick={save} title="Save body type" className="rounded p-0.5 text-[#3ecf8e] hover:bg-[#3ecf8e]/20"><Check className="h-3.5 w-3.5" /></button>
      <button type="button" data-testid={`${id}-edit-body-cancel-${v}`} onMouseDown={(e) => e.preventDefault()} onClick={onCancel} title="Cancel" className="rounded p-0.5 text-[#9aa0a8] hover:bg-white/10"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
};

// Model header in the Model dropdown. Owner: pencil → inline rename of every generation under this model; copy → the same
// inline field, but saves a duplicate of the whole model under the typed name (same glass); also a drop target while a year
// row is being dragged (dropProps / isOver come from the Picker).
// titles keep their full text and normal size on one line (the panel widens for long names); only beyond ~60 characters does the font ease down a little
const fit = (name) => { const px = Math.min(14, Math.max(10, 690 / (name.length * 0.78))); return px >= 14 ? undefined : { fontSize: px, letterSpacing: ".04em" }; };

export const GroupHeader = ({ id, group, base, showTrim = true, className, onRename, onDuplicate, dropProps = {}, isOver, lit = false, on = false }) => {
  const [name, setName] = useState(null);
  const [mode, setMode] = useState("rename");
  const [busy, setBusy] = useState(false);
  const split = base && base !== group && group.startsWith(base);
  const trim = split && showTrim ? group.slice(base.length).trim() : ""; // trims shown as their own rows → the header is the model alone
  const dup = mode === "duplicate";
  const save = async () => {
    const n = (name || "").trim();
    if (!n || n === group) { setName(null); return; }
    setBusy(true);
    try { await (dup ? onDuplicate : onRename)(group, n); setName(null); } finally { setBusy(false); }
  };
  if (name !== null) {
    return (
      <div className={`${className} flex items-center gap-1.5 !normal-case !tracking-normal`} data-testid={`${id}-group-${slug(group)}`} onClick={(e) => e.stopPropagation()}>
        {dup && <span className="flex flex-none items-center gap-1 text-[10px] font-semibold uppercase tracking-[.12em] text-[#3ecf8e]" data-testid={`${id}-duplicate-tag-${slug(group)}`}><Copy className="h-3 w-3" /> Copy as</span>}
        <input autoFocus value={name} maxLength={60} placeholder={dup ? "New model name" : undefined} data-testid={`${id}-${dup ? "duplicate" : "rename"}-input-${slug(group)}`} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") save(); if (e.key === "Escape") setName(null); }}
          className="field h-6 min-w-0 flex-1 py-0 text-[13px] font-medium uppercase tracking-[.1em] text-[#e8e9ec]" />
        <button type="button" data-testid={`${id}-${dup ? "duplicate" : "rename"}-save-${slug(group)}`} disabled={busy} onClick={save} title={dup ? "Create the copy" : "Save name"} className="rounded p-0.5 text-[#3ecf8e] hover:bg-[#3ecf8e]/20">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
        <button type="button" data-testid={`${id}-${dup ? "duplicate" : "rename"}-cancel-${slug(group)}`} onClick={() => setName(null)} title="Cancel" className="rounded p-0.5 text-[#9aa0a8] hover:bg-white/10"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <div className={`${className} group/hdr relative ${lit ? "is-lit" : ""} ${on ? "is-on" : ""} ${isOver ? "!bg-[#3ecf8e]/20 !text-[#3ecf8e] !shadow-[inset_0_0_0_1px_rgba(62,207,142,.7)]" : ""}`} data-testid={`${id}-group-${slug(group)}`} data-over={isOver || undefined} data-lit={lit || undefined} {...dropProps}>
      <span title={group} style={fit(split && !showTrim ? base : group)} className="picker-pop min-w-0 flex-1 truncate whitespace-nowrap text-center">
        {trim ? <>{base} <span data-testid={`${id}-group-trim-${slug(group)}`} className="font-normal tracking-[.03em] text-[#9ccbff]">{trim}</span></> : split && !showTrim ? base : group}
      </span>
      {(onRename || onDuplicate) && (
        <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-0 transition-opacity group-hover/hdr:opacity-100 focus-within:opacity-100">
          {onDuplicate && (
            <button type="button" data-testid={`${id}-duplicate-${slug(group)}`} title={`Duplicate "${group}" under another model name — same templates (e.g. T550 → T560)`}
              onClick={(e) => { e.stopPropagation(); setMode("duplicate"); setName(group); }}
              className="rounded p-0.5 text-[#9aa0a8] hover:bg-[#3ecf8e]/20 hover:text-[#3ecf8e]">
              <Copy className="h-3.5 w-3.5" />
            </button>
          )}
          {onRename && (
            <button type="button" data-testid={`${id}-rename-${slug(group)}`} title={`Rename "${group}" — every year listed under it takes the new name`}
              onClick={(e) => { e.stopPropagation(); setMode("rename"); setName(group); }}
              className="rounded p-0.5 text-[#9aa0a8] hover:bg-[#4c9ffe]/20 hover:text-[#4c9ffe]">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      )}
    </div>
  );
};
